// vim: set fdm=marker :
// {{{ import
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { createEntityAdapter } from '@reduxjs/toolkit';
import trelloApi from '@chronos/api-client/trelloApi';
import { getTrelloHeaders, trelloBaseApi } from './trelloBaseApi';
// }}}

const usersAdapter = createEntityAdapter();
export const initialUserState = usersAdapter.getInitialState();

const apiWithTag = trelloBaseApi.enhanceEndpoints({ addTagTypes: ['User'] });
const usersApi = apiWithTag.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query({
      queryFn: async ({ boardId, organizationId }) => {
        const commonParams = {
          filter: 'all',
          member: true,
          orgMemberType: true,
        };

        const [boardMemberships = [], orgMemberships = []] = await Promise.all(
          [
            ['getBoardMemberships', { boardId }],
            ...(organizationId
              ? [
                  [
                    'getOrganizationMemberships',
                    { idOrganization: organizationId },
                  ],
                ]
              : []),
          ].map(([method, params]) =>
            trelloApi[method]({ params: { ...commonParams, ...params } })
          )
        );

        const usersWithMemberType = [
          ...boardMemberships.map(({ member, memberType, orgMemberType }) => {
            return {
              ...member,
              memberType,
              orgMemberType,
              isAdmin: [memberType, orgMemberType].includes('admin'),
            };
          }),
          ...orgMemberships.map(({ member, memberType }) => {
            return {
              ...member,
              orgMemberType: memberType,
              isAdmin: memberType === 'admin',
            };
          }),
        ].reduce((acc, member) => {
          acc[member.id] = {
            ...(acc?.[member.id] ?? {}),
            ...member,
          };

          return acc;
        }, {});
        const users = Object.keys(usersWithMemberType).map(
          (userId) => usersWithMemberType[userId]
        );
        const normalized = usersAdapter.setAll(initialUserState, users);
        return { data: normalized };
      },
    }),
    getAuthUser: build.query({
      async queryFn(_, { getState }) {
        const userData = await trelloApi.getAuthMember({
          params: { fields: 'id,username,avatarUrl,fullName' },
          headers: getTrelloHeaders({ getState }),
        });
        return { data: userData };
      },
    }),
    getUser: build.query({
      async queryFn(
        { memberId, boardId, organizationId },
        { dispatch, getState }
      ) {
        const { data: { entities = {} } = initialUserState } =
          usersApi.endpoints.getUsers.select({
            boardId,
            organizationId,
          })(getState());

        if (entities[memberId]) {
          return { data: entities[memberId] };
        }

        let userData = {
          id: memberId,
          fullName: 'Unknown',
          username: 'unknown',
        };
        try {
          userData = await trelloApi.getMember({ params: { memberId } });
        } catch (err) {
          console.error(err);
        }

        dispatch(
          usersApi.util.updateQueryData(
            'getUsers',
            { boardId, organizationId },
            (draft) => {
              usersAdapter.setOne(draft, userData);
            }
          )
        );
        return { data: userData };
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetUsersQuery, useGetUserQuery, useGetAuthUserQuery } =
  usersApi;

export function useTrelloMembers({ boardId, organizationId }) {
  const outMembersList = useRef([]);
  const dispatch = useDispatch();

  const { data: { entities: usersEntities } = initialUserState } =
    useGetUsersQuery({
      boardId,
      organizationId,
    });

  // {{{ fetchOutMembers
  const fetchOutMembers = useDebouncedCallback(
    async () => {
      const outMembers = [...outMembersList.current];
      outMembersList.current = [];
      await Promise.all(
        outMembers
          .filter((mId) => !usersEntities[mId])
          .map((mId) =>
            dispatch(
              usersApi.endpoints.getUser.initiate({
                memberId: mId,
                boardId,
                organizationId,
              })
            )
          )
      );
    },
    500,
    {
      leading: false,
    }
  );

  const fetchOutTrelloMembers = useCallback(
    (ids) => {
      const idsObject = ids.reduce((acc, id) => {
        acc[id] = id;
        return acc;
      }, {});
      const outMembersObject = outMembersList.current.reduce((acc, id) => {
        acc[id] = id;
        return acc;
      }, {});
      outMembersList.current = Object.keys({
        ...idsObject,
        ...outMembersObject,
      });

      fetchOutMembers();
    },
    [fetchOutMembers]
  );
  // }}}

  return [fetchOutTrelloMembers];
}

export default usersApi;
